import React from 'react'
import { withStyles } from '@mui/styles'
import { Box, Typography, RadioGroup, Radio, FormControlLabel } from '@mui/material'

import { radioInputStyles } from './radio-input.styles'

/**
 * @typedef {object} RadioInputOption
 * @property {string | number} key - El valor único de la opción.
 * @property {string} value - El texto visible de la opción.
 */

/**
 * @typedef {object} RadioInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string} target.value - El valor de la opción seleccionada.
 */

/**
 * @typedef {object} RadioInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [readOnly=false] - Prop recibida pero no utilizada actualmente en la lógica del componente.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {RadioInputOption[]} [values=[]] - Array de opciones disponibles para seleccionar.
 * @property {string} [value] - El valor actual seleccionado.
 * @property {string} [title] - El título o etiqueta que se muestra sobre el grupo de radio buttons.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {function(RadioInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia la selección.
 */

/**
 * Un componente de grupo de radio buttons (botones de opción) para selección única.
 *
 * Presenta múltiples opciones en formato horizontal donde solo una puede ser seleccionada
 * a la vez. Ideal para opciones mutuamente excluyentes como Sí/No, género, tipo de documento, etc.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [genero, setGenero] = useState('');
 *
 *   const opciones = [
 *     { key: 'M', value: 'Masculino' },
 *     { key: 'F', value: 'Femenino' },
 *     { key: 'O', value: 'Otro' }
 *   ];
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' ahora es: ${e.target.value}`);
 *     setGenero(e.target.value);
 *   };
 *
 *   return (
 *     <RadioInput
 *       title="Género"
 *       name="genero"
 *       value={genero}
 *       values={opciones}
 *       onChange={handleChange}
 *       required
 *     />
 *   );
 * };
 *
 * @param {RadioInputProps} props - Las props del componente RadioInput.
 */
export const RadioInput = withStyles(radioInputStyles)(({
  classes,
  readOnly = false,
  values = [],
  ...props
}) => {
  return (
    <Box>
      {props.title ? (
        <Typography variant="subtitle1" gutterBottom>
          {props.title}
          {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
        </Typography>
      ) : null}
      <RadioGroup
        row
        value={props.value}
        onChange={props.onChange}
        style={{ paddingLeft: 10 }}
        name={props.name}
      >
        {values.map(({ key, value }) => {
          return (
            <FormControlLabel
              key={key}
              value={key}
              name={props.name}
              control={<Radio size="small" color="primary" />}
              label={
                <Typography variant="body1" color="textSecondary">
                  {value}
                </Typography>
              }
            />
          )
        })}
      </RadioGroup>
    </Box>
  )
})
